import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Square, Minus, Plus } from 'lucide-react';

// Web Audio scheduler timing constants (same as MetronomePage)
const LOOKAHEAD_MS = 25.0;
const SCHEDULE_AHEAD_SEC = 0.1;

/**
 * Inline metronome. Click play, sets a tempo (40-240 BPM, default
 * `initialBpm`), shows beat indicators. Stops automatically when the
 * component unmounts so closing a parent modal kills the audio.
 *
 * `accent` colours the active beat dot and the play button gradient.
 */
const MiniMetronome = ({
  initialBpm = 100,
  bpmMin = 40,
  bpmMax = 240,
  beatsPerBar = 4,
  accent = 'amber',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(initialBpm);
  const [currentBeat, setCurrentBeat] = useState(0);

  // Refs for the audio scheduler (avoids stale closures)
  const audioContextRef = useRef(null);
  const timerIdRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const currentBeatRef = useRef(0);
  const isPlayingRef = useRef(false);
  const bpmRef = useRef(initialBpm);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);

  // Re-anchor on initialBpm change (e.g. opening a different exercise)
  useEffect(() => { setBpm(initialBpm); }, [initialBpm]);

  const scheduler = useCallback(() => {
    const ctx = audioContextRef.current;
    if (!ctx || !isPlayingRef.current) return;

    const quarterNote = 60.0 / bpmRef.current;
    while (nextNoteTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD_SEC) {
      const isAccent = currentBeatRef.current === 0;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = isAccent ? 1000 : 800;
      osc.type = 'sine';
      const v = isAccent ? 0.7 : 0.5;
      gain.gain.setValueAtTime(v, nextNoteTimeRef.current);
      gain.gain.exponentialRampToValueAtTime(0.001, nextNoteTimeRef.current + 0.05);
      osc.start(nextNoteTimeRef.current);
      osc.stop(nextNoteTimeRef.current + 0.05);

      setCurrentBeat(currentBeatRef.current);
      currentBeatRef.current = (currentBeatRef.current + 1) % beatsPerBar;
      nextNoteTimeRef.current += quarterNote;
    }
  }, [beatsPerBar]);

  const start = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    isPlayingRef.current = true;
    setIsPlaying(true);
    currentBeatRef.current = 0;
    nextNoteTimeRef.current = audioContextRef.current.currentTime;
    timerIdRef.current = setInterval(scheduler, LOOKAHEAD_MS);
  }, [scheduler]);

  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentBeat(0);
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  const toggle = () => (isPlayingRef.current ? stop() : start());
  const adjust = (delta) => setBpm((b) => Math.max(bpmMin, Math.min(bpmMax, b + delta)));

  // Cleanup on unmount: stop scheduler and close the audio context so
  // the parent modal closing doesn't leak a clicking metronome.
  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      if (timerIdRef.current) clearInterval(timerIdRef.current);
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch (_) {}
        audioContextRef.current = null;
      }
    };
  }, []);

  const accentClasses = {
    amber: { dot: 'bg-amber', btn: 'from-amber to-orange-500' },
    blue: { dot: 'bg-cyan-400', btn: 'from-blue to-cyan-400' },
    purple: { dot: 'bg-purple-400', btn: 'from-purple-500 to-pink-500' },
    green: { dot: 'bg-green-400', btn: 'from-green-500 to-emerald-500' },
  }[accent] || { dot: 'bg-amber', btn: 'from-amber to-orange-500' };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          {Array.from({ length: beatsPerBar }).map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-100 ${
                isPlaying && currentBeat === i
                  ? `${accentClasses.dot} scale-150 shadow-glow`
                  : 'bg-white/15'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => adjust(-5)}
            className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
            aria-label="−5 BPM"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <div className="font-mono text-2xl font-black text-white tabular-nums w-14 text-center">
            {bpm}
          </div>
          <button
            onClick={() => adjust(5)}
            className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center"
            aria-label="+5 BPM"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={toggle}
            className={`ml-2 w-10 h-10 rounded-full bg-gradient-to-r ${accentClasses.btn} text-black flex items-center justify-center hover:scale-105 transition-transform`}
            aria-label={isPlaying ? 'Stop metronome' : 'Start metronome'}
          >
            {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
          </button>
        </div>
      </div>
      <input
        type="range"
        min={bpmMin}
        max={bpmMax}
        step={1}
        value={bpm}
        onChange={(e) => setBpm(Number(e.target.value))}
        className="w-full accent-cyan-400"
        aria-label="Tempo BPM"
      />
      <div className="flex justify-between text-[10px] text-zinc-500 mt-1 font-mono">
        <span>{bpmMin}</span>
        <span>BPM</span>
        <span>{bpmMax}</span>
      </div>
    </div>
  );
};

export default MiniMetronome;
